import React, { useMemo } from "react";
import { Box, Typography, Grid, Accordion, AccordionSummary, AccordionDetails, Chip, Stack } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ProducerCommissionCompanyOverride } from "../../interfaces/DTO/commissions/producerCommission";
import { MimshakData } from "../../interfaces/DTO/mimshakData";

interface Props {
  rows: ProducerCommissionCompanyOverride[];
  currentSum?: number;
  mimshaks?: MimshakData[];
  totalFundAllocation?: Record<number, number>;
}

interface GroupedOverrides {
  [rangeKey: string]: ProducerCommissionCompanyOverride[];
}

interface FundAllocation {
  [fundId: number]: number;
}

const CommissionListView: React.FC<Props> = ({ 
  rows, 
  currentSum, 
  mimshaks = [],
  totalFundAllocation 
}) => {
  // חלוקת ההחרגות לפי range_key
  const groupedOverrides: GroupedOverrides = useMemo(() => {
    const grouped: GroupedOverrides = {};
    
    rows.forEach(override => {
      const rangeKey = override.range_key || ""; // ריק עבור דיפולטי
      if (!grouped[rangeKey]) {
        grouped[rangeKey] = [];
      }
      grouped[rangeKey].push(override);
    });
    
    // מיון כל קבוצה לפי sort_order ואחרי זה לפי max_value
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => {
        // קודם לפי sort_order
        const orderA = a.sort_order ?? 0;
        const orderB = b.sort_order ?? 0;
        if (orderA !== orderB) return orderA - orderB;
        
        // אחרי זה לפי max_value
        const aVal = a.max_value ?? Number.POSITIVE_INFINITY;
        const bVal = b.max_value ?? Number.POSITIVE_INFINITY;
        return aVal - bVal;
      });
    });
    
    return grouped;
  }, [rows]);

  // חישוב התפלגות סכומים לפי fund_id
  const fundAllocation: FundAllocation = useMemo(() => {
    if (totalFundAllocation) {
      return totalFundAllocation;
    }
    
    if (!mimshaks.length || currentSum === undefined) {
      return { 0: currentSum ?? 0 };
    }

    // אוסף את כל ה-fund_ids הייחודיים
    const allFundIds = new Set<number>();
    mimshaks.forEach(mimshak => {
      if (mimshak.fund_ids?.length) {
        mimshak.fund_ids.forEach(id => allFundIds.add(id));
      } else {
        allFundIds.add(0); // דיפולטי
      }
    });

    // חלוקה פשוטה לפי מספר הקרנות (ניתן לשפר עם משקלים בפועל)
    const allocation: FundAllocation = {};
    if (allFundIds.size === 0) {
      allocation[0] = currentSum;
    } else if (allFundIds.size === 1) {
      const singleFund = Array.from(allFundIds)[0];
      allocation[singleFund] = currentSum;
    } else {
      const amountPerFund = currentSum / allFundIds.size;
      allFundIds.forEach(fundId => {
        allocation[fundId] = amountPerFund;
      });
    }
    
    return allocation;
  }, [mimshaks, currentSum, totalFundAllocation]);

  // מציאת המדרגה הרלוונטית עבור קבוצת fund_ids וסכום נתונים
  const findRelevantOverride = (
    overrides: ProducerCommissionCompanyOverride[], 
    fundIds: number[],
    amount: number
  ): ProducerCommissionCompanyOverride | null => {
    return overrides.find(override => {
      // בודק התאמה של fund_ids אם קיימים
      const fundMatch = !override.fund_ids?.length || 
        fundIds.some(id => override.fund_ids?.includes(id));
      
      // בודק התאמה של סכום
      const amountMatch = (override.min_value === null || override.min_value === undefined || amount >= override.min_value) &&
        (override.max_value === null || override.max_value === undefined || amount <= override.max_value);
      
      return fundMatch && amountMatch;
    }) ?? null;
  };

  // קביעה האם להציג קבוצה מסוימת
  const shouldShowGroup = (rangeKey: string, override: ProducerCommissionCompanyOverride): boolean => {
    if (!rangeKey) return true; // תמיד מציגים את הקבוצה הדיפולטית
    
    // אם יש fund_ids בהחרגה, בודקים אם יש חפיפה עם ההקצאה
    if (override.fund_ids?.length) {
      return override.fund_ids.some(fundId => Object.keys(fundAllocation).includes(fundId.toString()));
    }
    
    return true;
  };

  // יצירת כותרת קבוצה
  const getGroupTitle = (rangeKey: string, firstOverride: ProducerCommissionCompanyOverride): string => {
    if (!rangeKey) return "כללי";
    
    if (firstOverride.fund_ids?.length) {
      const fundNames = firstOverride.fund_ids.map(id => {
        // רק שם וערך, בשורות נפרדות
        const fundName = `קרן ${id}`;
        return fundName;
      }).join("\n");
      return fundNames;
    }
    
    return rangeKey;
  };

  // חישוב סכום מצטבר לקבוצה
  const getGroupAllocation = (override: ProducerCommissionCompanyOverride): number => {
    if (!override.fund_ids?.length) {
      return fundAllocation[0] ?? 0;
    }
    
    return override.fund_ids.reduce((sum, fundId) => {
      return sum + (fundAllocation[fundId] ?? 0);
    }, 0);
  };

  const renderOverrideGroup = (rangeKey: string, overrides: ProducerCommissionCompanyOverride[]) => {
    const firstOverride = overrides[0];
    if (!shouldShowGroup(rangeKey, firstOverride)) return null;
    
    const allocation = getGroupAllocation(firstOverride);
    const relevantFundIds = firstOverride.fund_ids || [0];
    const relevantOverride = findRelevantOverride(overrides, relevantFundIds, allocation);
    const groupTitle = getGroupTitle(rangeKey, firstOverride);
    
    return (
      <Box key={rangeKey} mb={3}>
        <Stack direction="column" spacing={1} mb={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2" fontWeight="bold">
              {groupTitle}
            </Typography>
            {allocation > 0 && (
              <Chip 
                label={`${allocation.toLocaleString()} ₪`} 
                size="small" 
                color={!rangeKey ? "default" : "primary"}
              />
            )}
          </Stack>
          {rangeKey && (
            <Chip 
              label={rangeKey} 
              size="small" 
              variant="outlined"
              color="primary"
            />
          )}
          {rangeKey && (
            <Typography variant="caption" color="text.secondary" sx={{ 
              fontSize: '0.75rem',
              fontStyle: 'italic'
            }}>
              * הסכום של כל הקרנות שנבחרו יסכם כדי למצוא את מדרגת דמי הניהול המתאימה
            </Typography>
          )}
        </Stack>
        
        <Grid container spacing={1}>
          {overrides.map((override, idx) => {
            const isHighlighted = override === relevantOverride;
            const isApplicable = allocation > 0 && (
              (override.min_value === null || allocation >= override.min_value) &&
              (override.max_value === null || allocation <= override.max_value)
            );
            
            return (
              <Grid item xs={3} key={`${rangeKey}-${idx}`}>
                <Box
                  p={1.5}
                  borderRadius={2}
                  bgcolor={
                    isHighlighted 
                      ? "success.100" 
                      : isApplicable 
                        ? "info.50"
                        : "grey.100"
                  }
                  border={
                    isHighlighted 
                      ? "2px solid green" 
                      : isApplicable
                        ? "1px solid #2196f3"
                        : "1px solid #ccc"
                  }
                  sx={{
                    transition: "all 0.2s ease",
                    "&:hover": {
                      boxShadow: 2
                    }
                  }}
                >
                  <Stack spacing={0.5} alignItems="center">
                    <Typography variant="body2" align="center" fontWeight="bold">
                      {override.max_value?.toLocaleString() ?? "∞"} ₪
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color={isHighlighted ? "success.main" : "text.secondary"} 
                      align="center"
                      fontWeight={isHighlighted ? "bold" : "normal"}
                    >
                      {(override.saving_fee ?? 0).toFixed(2)}%
                    </Typography>
                    {override.min_value !== null && override.min_value !== undefined && (
                      <Typography variant="caption" color="text.secondary" align="center">
                        מ-{override.min_value.toLocaleString()} ₪
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  // מיון קבוצות להצגה - דיפולטי (ריק) קודם, אחר כך לפי range_key
  const sortedGroupIds = useMemo(() => {
    return Object.keys(groupedOverrides)
      .filter(rangeKey => {
        const firstOverride = groupedOverrides[rangeKey][0];
        return shouldShowGroup(rangeKey, firstOverride);
      })
      .sort((a, b) => {
        if (!a && b) return -1; // ריק קודם
        if (a && !b) return 1;
        return a.localeCompare(b); // מיון לפי ABC
      });
  }, [groupedOverrides, fundAllocation]);

  // אם יש רק קבוצה אחת (דיפולטית), הצג בפורמט הישן
  if (sortedGroupIds.length === 1 && !sortedGroupIds[0]) {
    const defaultOverrides = groupedOverrides[""];
    const firstOverride = defaultOverrides[0];
    const allocation = getGroupAllocation(firstOverride);
    const relevantOverride = findRelevantOverride(defaultOverrides, [0], allocation);

    return (
      <Box mt={2}>
        <Typography variant="subtitle1" gutterBottom>
          מדרגות דמי ניהול לפי סכום: (עד סכום)
        </Typography>
        <Grid container spacing={1}>
          {defaultOverrides.map((override, idx) => {
            const isHighlighted = override === relevantOverride;
            return (
              <Grid item xs={3} key={idx}>
                <Box
                  p={1.5}
                  borderRadius={2}
                  bgcolor={isHighlighted ? "success.100" : "grey.100"}
                  border={isHighlighted ? "2px solid green" : "1px solid #ccc"}
                >
                  <Typography variant="body2" align="center" fontWeight="bold">
                    {override.max_value?.toLocaleString() ?? "∞"} ₪
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color={isHighlighted ? "success.main" : "text.secondary"} 
                    align="center"
                    fontWeight={isHighlighted ? "bold" : "normal"}
                  >
                    {(override.saving_fee ?? 0).toFixed(2)}%
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  }

  // מצב עם מספר קבוצות - הצגה באקורדיון או בקבוצות נפרדות
  return (
    <Box mt={2}>
      <Typography variant="subtitle1" gutterBottom>
        מדרגות דמי ניהול לפי קבוצות קרנות:
      </Typography>
      
      {sortedGroupIds.length > 1 ? (
        // אקורדיון אם יש יותר מקבוצה אחת
        <>
          {sortedGroupIds.map(rangeKey => {
            const overrides = groupedOverrides[rangeKey];
            const firstOverride = overrides[0];
            const allocation = getGroupAllocation(firstOverride);
            const groupTitle = getGroupTitle(rangeKey, firstOverride);
            
            return (
              <Accordion key={rangeKey} defaultExpanded={true}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="column" spacing={0.5}>
                    <Typography variant="subtitle2">
                      {groupTitle}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {allocation > 0 && (
                        <Chip 
                          label={`${allocation.toLocaleString()} ₪`}
                          size="small"
                          color={!rangeKey ? "default" : "primary"}
                        />
                      )}
                      {rangeKey && (
                        <Chip 
                          label={rangeKey} 
                          size="small" 
                          variant="outlined"
                          color="primary"
                        />
                      )}
                    </Stack>
                    {rangeKey && (
                      <Typography variant="caption" color="text.secondary" sx={{ 
                        fontSize: '0.75rem',
                        fontStyle: 'italic'
                      }}>
                        * הסכום של כל הקרנות שנבחרו יסכם כדי למצוא את מדרגת דמי הניהול המתאימה
                      </Typography>
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={1}>
                    {overrides.map((override, idx) => {
                      const relevantFundIds = firstOverride.fund_ids || [0];
                      const isHighlighted = findRelevantOverride(overrides, relevantFundIds, allocation) === override;
                      
                      return (
                        <Grid item xs={3} key={idx}>
                          <Box
                            p={1.5}
                            borderRadius={2}
                            bgcolor={isHighlighted ? "success.100" : "grey.100"}
                            border={isHighlighted ? "2px solid green" : "1px solid #ccc"}
                          >
                            <Typography variant="body2" align="center" fontWeight="bold">
                              {override.max_value?.toLocaleString() ?? "∞"} ₪
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color={isHighlighted ? "success.main" : "text.secondary"} 
                              align="center"
                              fontWeight={isHighlighted ? "bold" : "normal"}
                            >
                              {(override.saving_fee ?? 0).toFixed(2)}%
                            </Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </>
      ) : (
        // תצוגה פשוטה אם יש רק קבוצה אחת (לא דיפולטית)
        sortedGroupIds.map(rangeKey => renderOverrideGroup(rangeKey, groupedOverrides[rangeKey]))
      )}
      
      {/* הוספת מידע נוסף על ההתפלגות */}
      {Object.keys(fundAllocation).length > 1 && (
        <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
          <Typography variant="caption" color="text.secondary">
            התפלגות סכומים: {Object.entries(fundAllocation)
              .filter(([, amount]) => amount > 0)
              .map(([fundId, amount]) => 
                `${parseInt(fundId) === 0 ? 'כללי' : `קרן ${fundId}`}: ${amount.toLocaleString()} ₪`
              )
              .join(' | ')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CommissionListView;